<template>
    <div>
        <ExpandableCard
            :expand-id="abono.id"
            @expand="emit('expand', $event)"
        >
            <template #summaryIcon>
                <div class="tw:hidden tw:w-10 tw:h-10 tw:rounded-full tw:bg-slate-100 tw:sm:flex tw:items-center tw:justify-center tw:text-[var(--altcolor)]">
                    <i class="material-icons tw:text-xl">confirmation_number</i>
                </div>
            </template>

            <template #summaryContent>
                <div>
                    <h2 class="tw:text-base tw:font-extrabold tw:text-slate-900">{{ nombreCompleto }}</h2>
                    <p class="tw:text-slate-500 tw:text-sm tw:font-medium tw:m-0">
                        {{ $t('perfil.n_abonado') }}: <span class="tw:text-slate-900 tw:font-bold">{{ abono.n_abonado }}</span>
                    </p>
                </div>
            </template>

            <template #summaryActions>
                <div class="tw:flex tw:flex-col tw:items-end tw:gap-2">
                    <div class="tw:block tw:sm:hidden">
                        <BadgeFront
                            :variant="badgeEstado.variant"
                            :label="badgeEstado.label"
                            :dot="false"
                        />
                    </div>
                    <div class="tw:hidden tw:sm:block">
                        <BadgeFront
                            :variant="badgeEstado.variant"
                            :label="badgeEstado.label"
                            :icon="badgeEstado.icon"
                        />
                    </div>
                    <span
                        v-if="abono.lista?.nombre"
                        class="tw:text-[12px] tw:text-slate-500 tw:font-medium tw:leading-none"
                    >
                        {{ abono.lista.nombre }}
                    </span>
                </div>
            </template>

            <Skeleton v-if="loadingDetalle && !detalle" customClass="tw:shadow-none" padding="tw:p-0">
                <template #skeleton>
                    <section class="tw:flex tw:flex-col tw:lg:flex-row tw:gap-4">
                        <div class="tw:w-full tw:lg:w-[60%]">
                            <div class="tw:flex tw:justify-between tw:items-center">
                                <div class="tw:flex tw:flex-col tw:gap-2">
                                    <SkeletonItem class="tw:w-32" height="tw:h-4" />
                                    <SkeletonItem class="tw:w-32" height="tw:h-4"/>
                                </div>
                                <div class="tw:flex tw:gap-3">
                                    <div class="tw:flex tw:flex-col tw:gap-2">
                                        <SkeletonItem class="tw:w-20" height="tw:h-4" />
                                        <SkeletonItem class="tw:w-20" height="tw:h-4"/>
                                    </div>
                                    <SkeletonItem class="tw:w-14 tw:rounded-xl" height="tw:h-[22px]"/>

                                </div>
                            </div>
                            <div class="tw:flex tw:flex-col tw:sm:flex-row tw:gap-4 tw:mt-4">
                                <SkeletonItem class="tw:h-11 tw:w-auto tw:sm:w-full" />
                                <div class="tw:flex tw:sm:flex-row tw:w-full tw:gap-4">
                                    <SkeletonItem class="tw:h-11 tw:w-full" />
                                    <SkeletonItem class="tw:h-11 tw:w-full" />
                                </div>
                            </div>
                            <SkeletonItem class="tw:h-11 tw:mt-4" />
                        </div>
                        <SkeletonItem class="tw:hidden tw:lg:block tw:w-[1px] tw:mx-2" height="tw:h-56"/>
                        <div class="tw:w-full tw:lg:w-[35%]">
                            <div class="tw:flex tw:flex-col tw:gap-[18px]">
                                <SkeletonItem class="tw:w-28 " height="tw:h-3"/>
                                <div class="tw:flex tw:flex-col tw:gap-3">
                                    <SkeletonItem class="tw:w-full" height="tw:h-10" />
                                    <SkeletonItem class="tw:w-full" height="tw:h-10"/>
                                </div>
                                <SkeletonItem class="tw:w-28 " height="tw:h-4"/>
                                <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-3">
                                    <SkeletonItem class="tw:w-full" height="tw:h-10" />
                                    <SkeletonItem class="tw:w-full" height="tw:h-10"/>
                                </div>
                                <SkeletonItem class="tw:w-28 " height="tw:h-4"/>
                                <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-3">
                                    <SkeletonItem class="tw:w-full" height="tw:h-10" />
                                    <SkeletonItem class="tw:w-full" height="tw:h-10"/>
                                </div>
                            </div>
                        </div>
                    </section>
                </template>
            </Skeleton>

            <div v-else-if="detalle" class="tw:flex tw:flex-col tw:lg:flex-row tw:gap-6 tw:mb-7">
                <div class="tw:flex-1 tw:flex tw:flex-col tw:gap-4 tw:lg:gap-5">
                    <div class="tw:flex tw:sm:items-start tw:justify-between tw:gap-4">
                        <div class="tw:flex tw:flex-col tw:text-[12px] tw:font-bold tw:uppercase">
                            <span>{{ $t('perfil.detalles_abono') }}</span>
                            <span class="tw:text-slate-500 tw:text-sm tw:font-medium tw:capitalize">
                                {{ descuentoDescripcion }}
                            </span>
                        </div>
                        <div v-if="puedeDesmarcar" class="tw:flex tw:flex-row-reverse tw:gap-3 tw:items-start">
                            <ToggleSwitch
                                :value="true"
                                :loading="loadingDesmarcar"
                                variant="success"
                                size="sm"
                                :aria-label="$t('perfil.desactivar_renovacion_auto')"
                                @input="onRequestDesmarcar"
                            />
                            <div class="tw:flex tw:flex-col tw:text-[12px] tw:font-bold tw:uppercase">
                                <span>{{ $t('perfil.renovacion') }}</span>
                                <span>{{ $t('perfil.automatica') }}</span>
                            </div>
                        </div>
                        <div v-else class="tw:flex tw:items-start tw:gap-3">
                            <div class="tw:flex tw:flex-col tw:text-[12px] tw:font-bold tw:uppercase">
                                <span>{{ $t('perfil.renovacion') }}</span>
                                <span>{{ $t('perfil.automatica') }}</span>
                            </div>
                            <BadgeFront
                                variant="danger"
                                :label="$t('perfil.no')"
                                icon="cancel"
                            />
                        </div>
                    </div>

                    <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4">
                        <CardFront custom-classes="tw:px-3 tw:pt-3 tw:pb-2 tw:items-center">
                            <span class="tw:block tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:leading-none tw:text-slate-500">{{ $t('perfil.zona') }}</span>
                            <span class="tw:block tw:font-extrabold tw:text-slate-900 tw:tracking-wide tw:text-md tw:sm:text-lg tw:-mt-1">{{ asientoRegion || '—' }}</span>
                        </CardFront>
                        <div class="tw:grid tw:grid-cols-2 tw:gap-4">
                            <CardFront custom-classes="tw:px-3 tw:pt-3 tw:pb-2 tw:items-center">
                                <span class="tw:block tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:leading-none tw:text-slate-500">{{ $t('perfil.fila') }}</span>
                                <span class="tw:block tw:font-black tw:text-slate-900 tw:tracking-wide tw:text-md tw:sm:text-lg tw:-mt-1">{{ asientoFila || '—' }}</span>
                            </CardFront>
                            <CardFront custom-classes="tw:px-3 tw:pt-3 tw:pb-2 tw:items-center">
                                <span class="tw:block tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:leading-none tw:text-slate-500">{{ $t('perfil.butaca') }}</span>
                                <span class="tw:block tw:font-black tw:text-slate-900 tw:tracking-wide tw:text-md tw:sm:text-lg tw:-mt-1">{{ asientoButaca || '—' }}</span>
                            </CardFront>
                        </div>
                    </div>

                    <CardFront custom-classes="tw:px-3 tw:pt-3 tw:pb-2">
                        <span class="tw:block tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:leading-none tw:text-slate-500">{{ $t('perfil.fecha_alta') }}</span>
                        <span class="tw:block tw:font-bold tw:text-slate-900 tw:-mt-1">{{ fechaAntiguedad }}</span>
                    </CardFront>
                    <div v-if="detalle.plazos?.length">
                        <h3 class="tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:leading-none tw:text-slate-500">
                            {{ $t('perfil.plazos') }}
                        </h3>
                        <PlazosList
                            :plazos="detalle.plazos"
                            selectable
                            @plazo-toggled="plazo => $emit('plazo-toggled', { abonadoId: abono.id, plazoId: plazo.id, estadoPlazo: plazo.estado })"
                        />
                    </div>

                    <AsientosLiberados
                        v-if="detalle && 'asientos_liberados' in detalle"
                        :asientos="detalle.asientos_liberados"
                    />

                </div>

                <div class="tw:hidden tw:lg:block tw:w-px tw:bg-slate-100"></div>

                <div class="tw:lg:w-[35%] tw:flex tw:flex-col tw:gap-4 tw:lg:gap-5 tw:mb-1">
                    <h3 class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:leading-none tw:text-slate-500 tw:mb-0">{{ $t('perfil.principales') }}</h3>
                    <div class="tw:flex tw:flex-col tw:w-full tw:gap-3">
                        <Button
                            v-if="familiaAbonadosActiva"
                            custom-class="tw:w-auto"
                            variant="alt-soft"
                            @click="onFamiliaClick"
                            icon-name="group"
                            icon-name-class="tw:text-2xl"
                        >
                            {{ tieneFamilia ? $t('perfil.familia.ver') : $t('perfil.familia.crear') }}
                        </Button>
                        <Button
                            custom-class="tw:w-auto"
                            variant="alt-soft"
                            @click="onInvitaciones"
                            icon-name="card_giftcard"
                            icon-name-class="tw:text-2xl"
                        >
                            {{ $t('perfil.invitaciones') }}
                        </Button>
                    </div>

                    <h3 v-if="abono.activo && abono.activo_temporada_actual && (urlDescargar || !lfpId)" class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:leading-none tw:text-slate-500 tw:mb-0">{{ $t('perfil.acciones_rapidas') }}</h3>
                    <div class="tw:w-full tw:flex tw:flex-col tw:gap-4 tw:lg:gap-5">
                        <div class="tw:flex tw:gap-3">
                            <Button
                                v-if="urlDescargar && abono.activo && abono.activo_temporada_actual"
                                as="a"
                                :href="urlDescargar"
                                target="_blank"
                                variant="ghost-soft"
                                custom-class="tw:w-full tw:bg-transparent"
                                icon-name="file_download"
                                icon-name-class="tw:text-2xl"
                            >
                                {{ $t('perfil.pdf') }}
                            </Button>

                            <Button
                                v-if="urlPassbook && !lfpId && abono.activo && abono.activo_temporada_actual"
                                as="a"
                                :href="urlPassbook"
                                target="_blank"
                                variant="ghost-soft"
                                custom-class="tw:w-full tw:bg-transparent"
                                icon-name="account_balance_wallet"
                                icon-name-class="tw:text-2xl"
                            >
                                {{ $t('perfil.wallet') }}
                            </Button>
                        </div>
                        <h3 v-if="abono.activo && abono.activo_temporada_actual" class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-[0.075em] tw:leading-none tw:text-slate-500 tw:mb-0">{{ $t('perfil.gestion_asiento') }}</h3>
                        <div class="tw:flex tw:gap-3">
                            <Button
                                v-if="abono.activo && abono.activo_temporada_actual"
                                variant="ghost-soft"
                                custom-class="tw:w-full tw:bg-transparent"
                                icon-name="event_seat"
                                icon-name-class="tw:text-2xl"
                                data-testid="abono-card-btn-liberar"
                                @click="navegarALiberarAsiento"
                            >
                                {{ $t('perfil.liberar_asiento') }}
                            </Button>
                            <Button
                                v-if="abono.activo && abono.activo_temporada_actual"
                                variant="ghost-soft"
                                custom-class="tw:w-full tw:bg-transparent"
                                icon-name="arrow_forward"
                                icon-name-class="tw:text-2xl"
                                data-testid="abono-card-btn-ceder"
                                @click="navegarACederAsiento"
                            >
                                {{ $t('perfil.ceder_asiento') }}
                            </Button>
                            <Button
                                v-if="detalle?.puede_cambiar_asiento"
                                variant="ghost-soft"
                                custom-class="tw:w-full tw:bg-transparent"
                                icon-name="compare_arrows"
                                icon-name-class="tw:text-2xl"
                                data-testid="abono-card-btn-cambiar-asiento"
                                @click="navegarACambiarAsiento"
                            >
                                {{ $t('perfil.cambiar_asiento') }}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </ExpandableCard>

        <!-- Modal crear familia -->
        <FamiliaCreateModal
            v-if="showCreateModal"
            :abonado-id="abono.id"
            :crear-familia="apiCrearFamilia"
            @close="showCreateModal = false"
            @created="onFamiliaCreated"
        />
    </div>
</template>

<script setup>
    import { computed, toRef, ref } from 'vue';
    import store from '@/store';
    import { useAbonoCard } from '@/composables/front/perfil/useAbonoCard';
    import { t } from '@/utils/i18n-helper';
    import BadgeFront from '@/modules/front/ui/BadgeFront.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import ToggleSwitch from '@/modules/front/ui/ToggleSwitch.vue';
    import { Skeleton, SkeletonItem } from '@/modules/front/ui/skeleton';
    import PlazosList from '../plazos/PlazosList.vue';
    import AsientosLiberados from './AsientosLiberados.vue';
    import ExpandableCard from '@/modules/ui/ExpandableCard.vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import FamiliaCreateModal from '../familia/CreateModal.vue';
    import { useFamilia } from '@/composables/front/perfil/useFamilia';

    const props = defineProps({
        abono: {
            type: Object,
            required: true
        },
        /** Datos del endpoint de detalle — null mientras no se haya cargado */
        detalle: {
            type: Object,
            default: null
        },
        /** true mientras la petición de detalle está en curso para esta card */
        loadingDetalle: {
            type: Boolean,
            default: false
        },
        /** true mientras la petición de desmarcar renovación está en curso para esta card */
        loadingDesmarcar: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['request-desmarcar', 'expand', 'liberar-asiento', 'cambiar-asiento', 'ceder-asiento', 'invitaciones', 'ver-familias', 'plazo-toggled', 'familia-created']);

    const {
        nombreCompleto,
        estaRenovado,
        esTemporadaAnterior,
        puedeDesmarcar,
        asientoRegion,
        asientoFila,
        asientoButaca,
        descuentoDescripcion,
        fechaAntiguedad,
        urlDescargar,
        urlPassbook,
        lfpId,
    } = useAbonoCard(toRef(props, 'abono'), toRef(props, 'detalle'));

    const teatro = computed(() => store.getters.getTeatro);
    const familiaAbonadosActiva = computed(() => Boolean(teatro.value?.familia_abonados_activa));
    const tieneFamilia = computed(() =>
        props.detalle?.tiene_familia ?? props.abono?.tiene_familia ?? false
    );

    const { crearFamilia } = useFamilia();

    const showCreateModal = ref(false);

    const badgeEstado = computed(() => {
        if (esTemporadaAnterior.value) {
            return {
                variant: 'warning',
                label: t('perfil.activo', {}, 'Activo'),
                icon: 'history',
            };
        }
        if (estaRenovado.value) {
            return {
                variant: 'success',
                label: t('perfil.activo', {}, 'Activo'),
                icon: 'check_circle',
            };
        }
        return {
            variant: 'default',
            label: t('perfil.no_activo', {}, 'No activo'),
            icon: 'cancel',
        };
    });

    /**
     * El toggle solicita la confirmación al ancestro — no monta su propio modal.
     * abonos/List intercepta este evento y gestiona el único BaseModalFront compartido.
     */
    const onRequestDesmarcar = () => {
        emit('request-desmarcar', props.abono.id);
    };

    const navegarALiberarAsiento = () => {
        emit('liberar-asiento', props.abono.id);
    };

    /**
     * Emite el evento de invitaciones solo si el abono tiene invitaciones disponibles.
     * No usamos `disabled` nativo para que el tooltip funcione en hover incluso
     * cuando el botón no está activo.
     */
    const onInvitaciones = () => {
        emit('invitaciones', props.abono.id);
    };

    const onFamiliaClick = event => {
        event.stopPropagation();
        if (tieneFamilia.value) {
            onVerFamilias();
        } else {
            showCreateModal.value = true;
        }
    };

    const onVerFamilias = () => {
        emit('ver-familias', props.abono.id);
    };

    /**
     * Wrapper sobre el composable para inyectarlo como prop al CreateModal.
     * El modal no debe llamar a la API directamente — eso viola "UI is dumb".
     */
    const apiCrearFamilia = (abonadoId, nombre) => {
        return crearFamilia(nombre, abonadoId);
    };

    const onFamiliaCreated = () => {
        showCreateModal.value = false;
        // Invalidar cache para que loadDetalle refresque tiene_familia
        emit('familia-created', props.abono.id);
    };

    const navegarACederAsiento = () => {
        emit('ceder-asiento', props.abono.id);
    };

    const navegarACambiarAsiento = () => {
        emit('cambiar-asiento', props.abono.id);
    };
</script>
